/**
 * Утилиты для работы с PWA
 * - Установка приложения
 * - Управление Service Worker
 * - Push notifications
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Проверяет, является ли приложение установленным (standalone режим)
 */
export function isPWAInstalled(): boolean {
  // Проверяем различные способы определения standalone режима
  if (typeof window === 'undefined') return false;
  
  // iOS Safari
  if ((window.navigator as any).standalone) {
    return true;
  }
  
  // Android Chrome
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Проверка через наличие индикатора PWA
  if ((window as any).deferredPrompt) {
    return false; // Еще не установлено, но доступно
  }
  
  return false;
}

/**
 * Проверяет, поддерживается ли установка PWA
 */
export function isInstallPromptAvailable(): boolean {
  return 'onbeforeinstallprompt' in window || typeof (window as any).deferredPrompt !== 'undefined';
}

/**
 * Показывает промпт установки PWA
 */
export async function showInstallPrompt(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  
  const deferredPrompt = (window as any).deferredPrompt as BeforeInstallPromptEvent | undefined;
  
  if (!deferredPrompt) {
    console.warn('⚠️ Install prompt не доступен');
    return false;
  }
  
  try {
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    // Очищаем промпт после использования
    (window as any).deferredPrompt = null;
    
    return outcome === 'accepted';
  } catch (error) {
    console.error('❌ Ошибка показа install prompt:', error);
    return false;
  }
}

/**
 * Регистрирует Service Worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    console.warn('⚠️ Service Worker не поддерживается');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    
    console.log('✅ Service Worker зарегистрирован:', registration.scope);
    
    // Проверяем обновления
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        console.log('🔄 Обнаружена новая версия Service Worker');
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            console.log('✅ Новая версия готова к установке');
          }
        });
      }
    });
    
    return registration;
  } catch (error) {
    console.error('❌ Ошибка регистрации Service Worker:', error);
    return null;
  }
}

/**
 * Запрашивает разрешение на уведомления
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') {
    console.warn('⚠️ Notifications API не поддерживается');
    return 'denied';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  if (Notification.permission === 'denied') {
    console.warn('⚠️ Разрешение на уведомления было отклонено');
    return 'denied';
  }
  
  try {
    const permission = await Notification.requestPermission();
    console.log('📱 Разрешение на уведомления:', permission);
    return permission;
  } catch (error) {
    console.error('❌ Ошибка запроса разрешения на уведомления:', error);
    return 'denied';
  }
}

/**
 * Получает Push подписку (для будущего использования)
 */
export async function getPushSubscription(
  registration: ServiceWorkerRegistration,
  publicKey: string
): Promise<PushSubscription | null> {
  if (typeof PushManager === 'undefined') {
    console.warn('⚠️ Push API не поддерживается');
    return null;
  }
  
  try {
    // Проверяем существующую подписку
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Создаем новую подписку
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource
      });
    }
    
    console.log('✅ Push подписка получена:', subscription.endpoint);
    return subscription;
  } catch (error) {
    console.error('❌ Ошибка получения Push подписки:', error);
    return null;
  }
}

/**
 * Конвертирует Base64 URL в Uint8Array для VAPID ключа
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

/**
 * Отменяет Push подписку
 */
export async function unsubscribeFromPush(
  registration: ServiceWorkerRegistration
): Promise<boolean> {
  try {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      console.log('✅ Push подписка отменена');
      return true;
    }
    return false;
  } catch (error) {
    console.error('❌ Ошибка отмены Push подписки:', error);
    return false;
  }
}

